/**
 * The `/analytics` "Conversion events — Meta Pixel" chart: which of the
 * pixel's event names it counts.
 *
 * Until 2026-10-11 the chart summed EVERY event type of a day, so page views
 * and the site's own click events made up most of each bar (measured on the
 * snapshot of that day: 1,190 events in the window, 169 of them conversions).
 * The chart now counts conversions only; the table under it still lists every
 * event type.
 *
 * Meta's stats answer carries event names and counts and nothing that says
 * what an event means, so the list has to live here. It is Meta's own
 * published list of standard events, minus the two that record a VIEW
 * (`PageView`, `ViewContent`). An event name the site invents (a click, a
 * panel opening) is not on Meta's list and is therefore never a conversion
 * here; Meta may add a standard event, and then this list gains a line.
 */

/** Meta's standard events that record a visitor doing something, not seeing something. */
export const META_CONVERSION_EVENTS: ReadonlySet<string> = new Set([
  "AddPaymentInfo",
  "AddToCart",
  "AddToWishlist",
  "CompleteRegistration",
  "Contact",
  "CustomizeProduct",
  "Donate",
  "FindLocation",
  "InitiateCheckout",
  "Lead",
  "Purchase",
  "Schedule",
  "Search",
  "StartTrial",
  "SubmitApplication",
  "Subscribe",
]);

export function isMetaConversionEvent(event: string): boolean {
  return META_CONVERSION_EVENTS.has(event);
}

/**
 * One day's conversions, from that day's `{event name: count}` map as the
 * snapshot stores it. A count that is not a finite number adds nothing.
 */
export function sumMetaConversions(
  eventsOfDay: Readonly<Record<string, unknown>>,
): number {
  let total = 0;
  for (const [event, count] of Object.entries(eventsOfDay)) {
    if (!isMetaConversionEvent(event)) continue;
    if (typeof count === "number" && Number.isFinite(count)) total += count;
  }
  return total;
}
